import { ApiClient } from '@/libs/device-auth/services/authService';
import { CapacitorHttp, HttpOptions } from '@capacitor/core';
import { sessionService } from './sessionService';

export class CapacitorApiClient implements ApiClient {
  private async request<T>(
    method: string,
    url: string,
    options: {
      data?: Record<string, unknown>;
      params?: Record<string, string>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    } = {},
  ): Promise<T> {
    const { data, params, headers = {}, signal } = options;

    if (signal?.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }

    const httpOptions: HttpOptions = {
      url,
      method,
      headers,
      params,
      data,
    };

    const response = await CapacitorHttp.request(httpOptions);
    if (response.status === 401) {
      await sessionService.clearSession();
      return response.data as T;
    }

    return response.data as T;
  }

  async get<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    // Convert params to the format expected by Capacitor
    const capacitorParams = params
      ? Object.entries(params).reduce((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {} as Record<string, string>)
      : undefined;

    return this.request<T>('GET', url, {
      params: capacitorParams,
      headers,
      signal,
    });
  }

  async post<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>('POST', url, { data, headers, signal });
  }

  async put<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>('PUT', url, { data, headers, signal });
  }

  async delete<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    // Convert params to the format expected by Capacitor
    const capacitorParams = params
      ? Object.entries(params).reduce((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {} as Record<string, string>)
      : undefined;

    return this.request<T>('DELETE', url, {
      params: capacitorParams,
      headers,
      signal,
    });
  }
}
